import { isNil } from 'lodash';
// Import Types
import type { DataTableColumn, DeviceTabInfosType, DirectionType, DropdownOption, Sorting } from 'types';
// Import Enums
import {
    ApprovalRequestPriorityEnum,
    ApprovalRequestStateEnum,
    SaveModesEnum,
    SortOrderEnum,
    ObjectTypeEnum,
    DeviceTypeEnum,
    DisplayModeEnum,
    WhatsAppTemplateStatusEnum,
    CJVisualizationDueDateTypeEnum,
    CJVisualizationUnlockConditionTypeEnum,
} from 'enums';
// Import Constants
import l from './localizationLabels';
import PARTNER_SETTINGS from './partnerSettings';
import TimeZonePack from './timezonePack';

export const DEFAULT_SORTING_NAME = 'CreatedDate';
export const DEFAULT_SORTING_DIRECTION = 'desc' as DirectionType;
export const DEFAULT_SORTING_THEN_NAME = 'CreatedDate';
export const DEFAULT_SORTING_THEN_DIRECTION = 'desc' as DirectionType;

export const DEFAULT_SORTING: Readonly<Sorting> = { Name: DEFAULT_SORTING_NAME, Direction: DEFAULT_SORTING_DIRECTION };

// Remove all dateFormats except ISO, periodically
const DateFormat = 'DD-MM-YYYY';
const DateTimeFormat = 'DD-MM-YYYY  HH:mm:ss';
const DateTimeWithoutSecondsFormat = 'DD-MM-YYYY  HH:mm';
const MonthDayYearFormat = 'MM/DD/YYYY';
const DayMonthFormat = 'DD/MM';
const MonthDayYearWithTimeFormat = 'MM/DD/YYYY HH:mm';
const TimeFormat = 'HH:mm:ss';
const TimeFormatWithoutSeconds = 'HH:mm';
const DateToISOStringDateFormat = 'YYYY-MM-DD';
const DateToISOStringDateFormatWithMonthName = 'YYYY MMM D';
const DateToISOStringFormat = 'YYYY-MM-DDTHH:mm:ss[Z]';
const DateToISOStringFormatWithMilliseconds = 'YYYY-MM-DDTHH:mm:ss.SSS[Z]';
const DateToISOStringWithoutTimezoneFormat = 'YYYY-MM-DDTHH:mm:ss';

const ZERO_TIME_WITH_SECONDS = '00:00:00';
const ZERO_TIME_WITHOUT_SECONDS = '00:00';

const oneHourInMilliseconds = 3600000;
const oneDayMilliseconds = 86400000;
const validDaysCount = 92;
const dailyViewLimitInWeeks = 5;

const BASE_DOMAIN_PLACEHOLDER = '{BaseDomain}';

const FilterHolderFields = {
    textInput: {
        type: 'textInput',
        defaultValue: '',
    },
    select: {
        type: 'select',
        defaultValue: undefined, // core default value is undefined
    },
    rangeDate: {
        type: 'rangeDate',
        defaultValue: [null, null],
    },
    singleDate: {
        type: 'singleDate',
        defaultValue: [null],
    },
    multiSelect: {
        type: 'multiSelect',
        defaultValue: [],
    },
    checkbox: {
        type: 'checkbox',
        defaultValue: null,
    },
    multiSelectAutocomplete: {
        type: 'multiSelectAutocomplete',
        defaultValue: [],
    },
    autocompleteOptionList: {
        type: 'autocompleteOptionList',
        defaultValue: [],
    },
    menuItemMultiSelect: {
        type: 'menuItemMultiSelect',
        defaultValue: [],
    },
};

const ExportFileFormat = [
    { label: l.Excel, value: 0 },
    { label: l.CSV, value: 1 },
];

const ExportFileFormatLabels = {
    0: 'Excel',
    1: 'CSV',
    Excel: 0,
    CSV: 1,
};

const SegmentTypesLabels = {
    0: 'Dynamic',
    1: 'Static',
    3: 'Composite',
    Dynamic: 0,
    Static: 1,
    Composite: 3,
} as const;

const SegmentTypeIcons = {
    [SegmentTypesLabels.Dynamic]: 'bc-icon-dynamic',
    [SegmentTypesLabels.Static]: 'bc-icon-static',
    [SegmentTypesLabels.Composite]: 'bc-icon-composite',
};

const SegmentTypeIconClasses = {
    [SegmentTypesLabels.Dynamic]: 'bc-icon-dynamic crm-object-type-icon dynamic',
    [SegmentTypesLabels.Static]: 'bc-icon-static crm-object-type-icon static',
    [SegmentTypesLabels.Composite]: 'bc-icon-composite crm-object-type-icon composite',
};

const MenuItemsTypes = {
    0: 'Query',
    1: 'Classifier',
    2: 'Custom',
    3: 'Multifield',
    4: 'Provider',
    Query: 0,
    Classifier: 1,
    Custom: 2,
    Multifield: 3,
    Provider: 4,
};

const WhatsAppTemplateStatusLabels = {
    [WhatsAppTemplateStatusEnum.Approved]: l.Approved,
    [WhatsAppTemplateStatusEnum.Pending]: l.Pending,
    [WhatsAppTemplateStatusEnum.Rejected]: l.Rejected,
};

const MenuItemsLabels = [
    {
        label: 'Query',
        value: 0,
    },
    {
        label: 'Classifier',
        value: 1,
    },
    {
        label: 'Custom',
        value: 2,
    },
    {
        label: 'Multifield',
        value: 3,
    },
    {
        label: 'Provider',
        value: 4,
    },
];

const EventLabels = {
    1: l.BetWin,
    2: l.WithdrawAprove,
    3: l.WithdrawRejected,
    4: l.MatchStarted,
    5: l.MatchStartedWithPrices,
    6: l.MatchAboutStart,
    7: l.MatchAboutStartWithPrices,
    8: l.MatchEnded,
    9: l.FootballGoal,
    10: l.FootballPenaltyStarted,
    11: l.Withdrawal,
    12: l.Deposit,
    13: l.FootballCorner,
    14: l.FootballYellowCard,
    15: l.FootballRedCard,
    16: l.LoyaltyPointsEarned,
};

const SegmentTypes = [
    {
        label: 'Dynamic',
        value: 0,
    },
    {
        label: 'Static',
        value: 1,
    },
    {
        label: 'Composite',
        value: 3,
    },
];

const CompositeSegmentOperation = {
    1: 'Union',
    2: 'Intersect',
    3: 'Difference',
    Union: 1,
    Intersect: 2,
    Difference: 3,
} as const;

const TemplateTypesLabels = {
    0: 'Email',
    1: 'Internal',
    2: 'SMS',
    3: 'PopUp',
    4: 'Telegram',
    7: 'PushNotification',
    8: 'CMSPopUp',
    9: 'Viber',
    10: 'Hoory',
    12: 'DynamicPopUp',
    14: 'WhatsApp',
    15: 'CMSBanner',
    16: 'WhatsAppProvider',
    Email: 0,
    Internal: 1,
    SMS: 2,
    PopUp: 3,
    Telegram: 4,
    PushNotification: 7,
    CMSPopUp: 8,
    Viber: 9,
    Hoory: 10,
    DynamicPopUp: 12,
    WhatsApp: 14,
    CMSBanner: 15,
    WhatsAppProvider: 16,
};

const TemplateTypeIcons = {
    [TemplateTypesLabels.Email]: 'bc-icon-send-email-block',
    [TemplateTypesLabels.Internal]: 'bc-icon-sms',
    [TemplateTypesLabels.SMS]: 'bc-icon-message-closed',
    [TemplateTypesLabels.PopUp]: 'bc-icon-send-pop-up-block',
    [TemplateTypesLabels.DynamicPopUp]: 'bc-icon-send-pop-up-block',
    [TemplateTypesLabels.Telegram]: 'bc-icon-send-telegram-message-block',
    [TemplateTypesLabels.PushNotification]: 'bc-icon-bell-outline',
    [TemplateTypesLabels.CMSPopUp]: 'bc-icon-send-pop-up-block',
    [TemplateTypesLabels.CMSBanner]: 'bc-icon-desktop-preview',
    [TemplateTypesLabels.Viber]: 'bc-icon-viber',
    [TemplateTypesLabels.Hoory]: 'bc-icon-hoory',
    [TemplateTypesLabels.WhatsApp]: 'bc-icon-whatsapp',
    [TemplateTypesLabels.WhatsAppProvider]: 'bc-icon-whatsapp',
};

const DeliveryMethodTypes = { ...TemplateTypesLabels, 5: 'FTP', FTP: 5, 6: 'SFTP', SFTP: 6 };

const PopUpUrlTypes = {
    Any: 0,
    Is: 1,
    IsNot: 2,
    StartsWith: 3,
    EndsWith: 4,
    Contains: 5,
    DoesNotContain: 6,
    0: 'Any',
    1: 'Is',
    2: 'IsNot',
    3: 'StartsWith',
    4: 'EndsWith',
    5: 'Contains',
    6: 'DoesNotContain',
};

const BonusTypes = [
    { label: l.DepositBonus, value: 2 },
    { label: l.NoDepositBonus, value: 3 },
    { label: l.CorrectionBonus, value: 4 },
    { label: l.FreeSpin, value: 5 },
    { label: l.FreeBet, value: 6 },
    { label: l.SkillGames, value: 8 },
    { label: l.BoostBonus, value: 9 },
    { label: l.FreeChip, value: 10 },
    { label: l.LoyaltyPoint, value: 12 },
    { label: l.CashBonus, value: 13 },
    { label: l.NFTBonus, value: 14 },
    { label: l.FrenchFreeBet, value: 15 },
    { label: l.LoyaltyBenefit, value: 17 },
    { label: l.WonderWheelBonus, value: 18 },
    { label: l.NftVoucherBonus, value: 19 },
    { label: l.PiePoints, value: 20 },
];

const BonusTypesLabels = {
    2: l.DepositBonus,
    3: l.NoDepositBonus,
    4: l.CorrectionBonus,
    5: l.FreeSpin,
    6: l.FreeBet,
    8: l.SkillGames,
    9: l.BoostBonus,
    10: l.FreeChip,
    12: l.LoyaltyPoint,
    13: l.CashBonus,
    14: l.NFTBonus,
    15: l.FrenchFreeBet,
    17: l.LoyaltyBenefit,
    18: l.WonderWheelSpin,
    19: l.NftVoucherBonus,
    20: l.PiePoints,
    DepositBonus: 2,
    NoDepositBonus: 3,
    CorrectionBonus: 4,
    FreeSpin: 5,
    FreeBet: 6,
    SkillGames: 8,
    BoostBonus: 9,
    FreeChip: 10,
    LoyaltyPoint: 12,
    CashBonus: 13,
    NFTBonus: 14,
    FrenchFreeBet: 15,
    LoyaltyBenefit: 17,
    WonderWheelSpin: 18,
    NftVoucherBonus: 19,
    PiePoints: 20,
};

const BonusLabels = {
    DepositBonus: l.DepositBonus,
    NoDepositBonus: l.NoDepositBonus,
    CorrectionBonus: l.CorrectionBonus,
    FreeSpin: l.FreeSpin,
    FreeBet: l.FreeBet,
    SkillGames: l.SkillGames,
    BoostBonus: l.BoostBonus,
    FreeChip: l.FreeChip,
    LoyaltyPoint: l.LoyaltyPoint,
    CashBonus: l.CashBonus,
    NFTBonus: l.NFTBonus,
    FrenchFreeBet: l.FrenchFreeBet,
    LoyaltyBenefit: l.LoyaltyBenefit,
    WonderWheelSpin: l.WonderWheelSpin,
    NftVoucherBonus: l.NftVoucherBonus,
};

//TODO: when the using file type is changed to ts, please convert ExpirationLabels to Expiration enum.
const ExpirationLabels = {
    Type: 'expirationType',
    Period: 'expirationPeriod',
    Date: 'expirationDate',
    HasExpiration: 'hasExpiration',
};

const BonusTypesIcons = {
    [BonusTypesLabels.DepositBonus]: 'bc-icon-crm-deposit-bonus',
    [BonusTypesLabels.SkillGames]: 'bc-icon-crm-skill-games',
    [BonusTypesLabels.FreeBet]: 'bc-icon-crm-free-bet',
    [BonusTypesLabels.FreeSpin]: 'bc-icon-crm-free-spin',
    [BonusTypesLabels.LoyaltyBenefit]: 'bc-icon-loyalty-point',
};

const EntityStatesLabels = {
    1: 'Used',
    0: 'NotUsed',
    Used: 1,
    NotUsed: 0,
};

const AwardUsageTypeLabels = {
    0: l.CashWithdrawal,
    1: l.NFTShop,
    [l.CashWithdrawal]: 0,
    [l.NFTShop]: 1,
};

const AwardUsageTypeLabelValueList = [
    {
        label: l.CashWithdrawal,
        value: 0,
    },
    {
        label: l.NFTShop,
        value: 1,
    },
];

const EntityStates = [
    {
        label: 'Used',
        value: '1',
    },
    {
        label: 'NotUsed',
        value: '0',
    },
];

const ArchiveUnarchiveTypes = {
    Archive: false,
    UnArchive: true,
};

const StateModesLabels = {
    0: l.All,
    1: l.Live,
    2: l.Test,
    3: l.Draft,
    5: l.Emulate,
    All: 0,
    Live: 1,
    Test: 2,
    Draft: 3,
    Emulate: 5,
};

const StateModes = [
    {
        label: l.Live,
        value: '1',
    },
];

const ArchiveStates = [
    {
        label: l.Yes,
        value: false,
    },
    {
        label: l.No,
        value: true,
    },
];

export const IsAssignedStates = [
    {
        label: l.Yes,
        value: false,
    },
    {
        label: l.No,
        value: true,
    },
];

const FullStateModes = [
    ...StateModes,
    {
        label: l.Draft,
        value: '3',
    },
];

const CustomersTypesLabels = {
    All: null,
    Customers: '0',
    NonCustomers: '1',
};

const CustomersTypes = [
    {
        label: l.Customers,
        value: CustomersTypesLabels.Customers,
    },
    {
        label: l.NonCustomers,
        value: CustomersTypesLabels.NonCustomers,
    },
];

export const SupportedLanguages = [
    {
        DisplayName: 'English',
        Id: 'en',
        IsSelected: false,
        Name: 'English',
    },
    {
        DisplayName: 'Russian',
        Id: 'ru',
        IsSelected: false,
        Name: 'Russian',
    },
    {
        DisplayName: 'Spanish',
        Id: 'es',
        IsSelected: false,
        Name: 'Spanish',
    },
    {
        DisplayName: 'Turkish',
        Id: 'tr',
        IsSelected: false,
        Name: 'Turkish',
    },
    {
        DisplayName: 'Chinese',
        Id: 'zh',
        IsSelected: false,
        Name: 'Chinese',
    },
    {
        DisplayName: 'Korean',
        Id: 'ko',
        IsSelected: false,
        Name: 'Korean',
    },
    {
        DisplayName: 'German',
        Id: 'de',
        IsSelected: false,
        Name: 'German',
    },
    {
        DisplayName: 'France',
        Id: 'fr',
        IsSelected: false,
        Name: 'France',
    },
    {
        DisplayName: 'Hindi',
        Id: 'hi',
        IsSelected: false,
        Name: 'Hindi',
    },
    {
        DisplayName: 'Portuguese',
        Id: 'pt',
        IsSelected: false,
        Name: 'Portuguese',
    },
    {
        DisplayName: 'Brazilian Portuguese',
        Id: 'pb',
        IsSelected: false,
        Name: 'Brazilian Portuguese',
    },
];

const AlertTypes = {
    warning: 'warning',
    error: 'error',
    success: 'success',
    info: 'info',
    danger: 'danger',
} as const;

const DefinitionTypes = {
    predefined: 1,
    notPredefined: 0,
};

const ModalsActions = {
    CONVERT_TO_REPORT: 'CONVERT_TO_REPORT',
    DELETE: 'DELETE',
    ARCHIVE: 'ARCHIVE',
    UNARCHIVE: 'UNARCHIVE',
    CLONE: 'CLONE',
    EDIT: 'EDIT',
    CLOSE: 'CLOSE',
    TEST_TEMPLATE: 'TEST_TEMPLATE',
    STOP: 'STOP',
    CUSTOMER_JOURNEY_STOP: 'CUSTOMER_JOURNEY_STOP',
    SCHEDULE_REPORT: 'SCHEDULE_REPORT',
    UNSCHEDULE_REPORT: 'UNSCHEDULE_REPORT',
    EXECUTE_REPORT: 'EXECUTE_REPORT',
    SET_GOAL: 'SET_GOAL',
    DELETE_GOAL: 'DELETE_GOAL',
    START: 'START',
    RESUME: 'RESUME',
    SETDEFAULT: 'SETDEFAULT',
    SAVE: 'SAVE',
    RESEND: 'RESEND',
    UPDATE_PARTNER_SETTINGS: 'UPDATE_PARTNER_SETTINGS',
    SET_TAG: 'SET_TAG',
    REMOVE_TAG: 'REMOVE_TAG',
    STOP_AB_TEST: 'STOP_AB_TEST',
    MAKE_PUBLIC: 'MAKE_PUBLIC',
    MAKE_PRIVATE: 'MAKE_PRIVATE',
    PUBLISH: 'PUBLISH',
    UNPUBLISH: 'UNPUBLISH',
} as const;

const FilterComparisons = {
    Equals: 0,
    NotEquals: 1,
    Like: 2,
    NotLike: 3,
    GreaterThan: 4,
    GreaterOrEquals: 5,
    LessThan: 6,
    LessOrEquals: 7,
    In: 8,
    Between: 9,
    BeginWith: 10,
    EndWith: 11,
    NotIn: 12,
    NotBetween: 13,
    NotBeginWith: 14,
    NotEndWith: 15,
    Exclude: 16,
    IsBlank: 18,
};

const LabelsModalViews = {
    manageLabelsView: 'manageLabelsView',
    setLabelView: 'setLabelView',
    editLabelView: 'editLabelView',
    createLabelView: 'createLabelView',
} as const;

const TagsModalViews = {
    setTagsView: 'setTagsView',
    createTagView: 'createTagView',
};

const ScheduleModalViews = {
    mainView: 'mainView',
    hourlyView: 'hourlyView',
    dailyView: 'dailyView',
    weeklyView: 'weeklyView',
    monthlyView: 'monthlyView',
    annuallyView: 'annuallyView',
    notRepeatView: 'notRepeatView',
};

const RepeatingScheduleLabels = {
    [ScheduleModalViews.hourlyView]: l.Hourly,
    [ScheduleModalViews.dailyView]: l.Daily,
    [ScheduleModalViews.weeklyView]: l.Weekly,
    [ScheduleModalViews.monthlyView]: l.Monthly,
    [ScheduleModalViews.annuallyView]: l.Annually,
    [ScheduleModalViews.notRepeatView]: l.OneTime,
};

const TargetTypeEnum = {
    None: 0,
    Template: 1,
    Segment: 2,
    Campaign: 3,
    AdHocReport: 4,
    OCampaign: 5,
    Schedule: 6,
    Bonus: 7,
    AdHocReportResult: 8,
    MessageSenderProfile: 9,
    CustomCampaign: 10,
    CustomReport: 11,
    PartnerSetting: 12,
    TargetTypeState: 13,
    CampaignGoal: 14,
    TriggeredCampaign: 15,
    NonCustomer: 16,
    PushCampaign: 17,
    Workflow: 18,
    Report: 19,
};

const DynamicPopUpDeviceTypesLabels = {
    Desktop: '1',
    Mobile: '2',
    Both: '3',
    1: 'Desktop',
    2: 'Mobile',
    3: 'Both',
};

const DisplayModeStyles = {
    [DisplayModeEnum.LightMode]: 'body {color:#000000}',
    [DisplayModeEnum.DarkMode]: 'body {color:#ffffff}',
};

const DisplayModeClassNames = {
    [DisplayModeEnum.LightMode]: 'for-device-light-mode',
    [DisplayModeEnum.DarkMode]: 'for-device-dark-mode',
};

const DeviceTypeClassNames = {
    [DeviceTypeEnum.Mobile]: 'mobile-view',
    [DeviceTypeEnum.Tablet]: 'tablet-view',
    [DeviceTypeEnum.Desktop]: 'desktop-view',
};

const ErrorShowType = {
    Toaster: 'Toaster',
    Default: 'Default',
};

const ListRequestDefaultParams = {
    filters: [],
    paging: {
        PageSize: 10,
        PageNumber: 1,
    },
    sorting: DEFAULT_SORTING,
};

const CustomerJourneyStatusLabels = {
    All: '0',
    InProgress: '1',
    Completed: '2',
    Suspended: '3',
    NonStarted: '4',
    Failed: '5',
    Invalid: '7',
    Starting: '8',
    Updating: '9',
    Resuming: '10',
    Suspending: '11',
    0: l.All,
    1: l.InProgress,
    2: l.Completed,
    3: l.Suspended,
    4: l.NonStarted,
    5: l.Failed,
    7: l.Invalid,
    8: l.Starting,
    9: l.Updating,
    10: l.Resuming,
    11: l.Suspending,
};

// TODO check if is needed
const PromoConditionLabels = {
    Completed: '1',
    Stopped: '2',
    Suspended: '3',
    Pending: '4',
    InProgress: '5',
    Failed: '6',
    1: l.Completed,
    2: l.Stoped,
    3: l.Suspended,
    4: l.Pending,
    5: l.InProgress,
    6: l.Failed,
};

const PromotionStatusColors = {
    [PromoConditionLabels.Completed]: '#6fb873',
    [PromoConditionLabels.Stopped]: '#9171cb',
    [PromoConditionLabels.Suspended]: '#f48a3a',
    [PromoConditionLabels.Pending]: '#898989',
    [PromoConditionLabels.InProgress]: '#207ede',
    [PromoConditionLabels.Failed]: '#df2f4d',
};

const AvailabilityColors = {
    Available: '#1E90FF',
    Unavailable: '#FF0000',
};

const CustomerJourneyWorkingState = {
    Disabled: 1 << 17,
    Skipped: (1 << 1) + 4,
};

const CustomerJourneyStatus = [
    {
        label: l.InProgress,
        value: CustomerJourneyStatusLabels.InProgress,
    },
    {
        label: l.Completed,
        value: CustomerJourneyStatusLabels.Completed,
    },
    {
        label: l.Suspended,
        value: CustomerJourneyStatusLabels.Suspended,
    },
    {
        label: l.NonStarted,
        value: CustomerJourneyStatusLabels.NonStarted,
    },
    {
        label: l.Failed,
        value: CustomerJourneyStatusLabels.Failed,
    },
    {
        label: l.Invalid,
        value: CustomerJourneyStatusLabels.Invalid,
    },
    {
        label: l.Starting,
        value: CustomerJourneyStatusLabels.Starting,
    },
    {
        label: l.Updating,
        value: CustomerJourneyStatusLabels.Updating,
    },
    {
        label: l.Resuming,
        value: CustomerJourneyStatusLabels.Resuming,
    },
    {
        label: l.Suspending,
        value: CustomerJourneyStatusLabels.Suspending,
    },
];

const CampaignType = {
    AddHoc: '0',
    Ongoing: '1',
    Custom: '2',
    Triggered: '3',
    Push: '4',
    WorkFlow: '5',
    0: 'AddHoc',
    1: 'Ongoing',
    2: 'Custom',
    3: 'Triggered',
    4: 'Push',
    5: 'WorkFlow',
};

const PromotionTypeLabels = {
    0: l.Campaign,
    5: l.CustomerJourney,
    8: l.ActionCampaign,
    Campaign: 0,
    CustomerJourney: 5,
    ActionCampaign: 8,
};

const ApprovalRequestPriorityLabels = {
    [ApprovalRequestPriorityEnum.Low]: l.Low,
    [ApprovalRequestPriorityEnum.Normal]: l.Normal,
    [ApprovalRequestPriorityEnum.High]: l.High,
};

const CampaignStatusLabels = {
    Scheduled: '0',
    Idle: '1',
    InProgress: '2',
    Completed: '3',
    Stopped: '5',
    All: '6',
    Error: '7',
    0: l.Scheduled,
    1: l.Idle,
    2: l.InProgress,
    3: l.Completed,
    5: l.Stoped,
    6: l.All,
    7: l.Error,
};

const ApprovalRequestStateLabels = {
    [ApprovalRequestStateEnum.Pending]: l.Pending,
    [ApprovalRequestStateEnum.Approved]: l.Approved,
    [ApprovalRequestStateEnum.Rejected]: l.Rejected,
    [ApprovalRequestStateEnum.Cancelled]: l.Cancelled,
    [ApprovalRequestStateEnum.Missed]: l.Missed,
};

const ApprovalRequestStateOptions: DropdownOption[] = [
    { label: l.Pending, value: ApprovalRequestStateEnum.Pending },
    { label: l.Approved, value: ApprovalRequestStateEnum.Approved },
    { label: l.Rejected, value: ApprovalRequestStateEnum.Rejected },
    { label: l.Cancelled, value: ApprovalRequestStateEnum.Cancelled },
    { label: l.Missed, value: ApprovalRequestStateEnum.Missed },
];

const ApprovalRequestStateColors = {
    [ApprovalRequestStateEnum.Pending]: 'warning',
    [ApprovalRequestStateEnum.Approved]: 'confirm',
    [ApprovalRequestStateEnum.Rejected]: 'danger',
    [ApprovalRequestStateEnum.Cancelled]: 'foreground',
    [ApprovalRequestStateEnum.Missed]: 'rgba(223, 63, 0, 0.6)', // #DF3F00
};

const ApprovalRequestStateAlertTypes = {
    [ApprovalRequestStateEnum.Pending]: 'warning',
    [ApprovalRequestStateEnum.Approved]: 'success',
    [ApprovalRequestStateEnum.Rejected]: 'error',
    [ApprovalRequestStateEnum.Cancelled]: 'note',
    [ApprovalRequestStateEnum.Missed]: 'message',
};

const ApprovalRequestStateMessages = {
    [ApprovalRequestStateEnum.Pending]: l.PendingApprovalRequestMessage,
    [ApprovalRequestStateEnum.Approved]: l.ApprovedApprovalRequestMessage,
    [ApprovalRequestStateEnum.Rejected]: l.RejectedApprovalRequestMessage,
    [ApprovalRequestStateEnum.Cancelled]: l.CancelledApprovalRequestMessage,
    [ApprovalRequestStateEnum.Missed]: l.MissedApprovalRequestMessage,
};

const CampaignStatusColors = {
    0: '--status-label-scheduled-color',
    1: '--status-label-idle-color',
    2: '--status-label-in-progress-color',
    3: '--status-label-completed-color',
    4: '--status-label-sent-color',
    5: '--status-label-stopped-color',
    6: '--status-label-error-color',
    7: '--status-label-error-color',
};

const ColorVars = {
    danger: 'var(--danger)',
};

const CampaignStatus = [
    {
        label: l.Scheduled,
        value: CampaignStatusLabels.Scheduled,
    },
    {
        label: l.Idle,
        value: CampaignStatusLabels.Idle,
    },
    {
        label: l.InProgress,
        value: CampaignStatusLabels.InProgress,
    },
    {
        label: l.Completed,
        value: CampaignStatusLabels.Completed,
    },
    {
        label: l.Stoped,
        value: CampaignStatusLabels.Stopped,
    },
    {
        label: l.Error,
        value: CampaignStatusLabels.Error,
    },
];

const BOOLEAN_DROPDOWN_OPTIONS_FOR_FILTER = [
    {
        label: l.Yes,
        value: '1',
    },
    {
        label: l.No,
        value: '0',
    },
] as const;

const PageValidationTypes = {
    DynamicSegmentPage: 'DynamicSegmentPage',
    StaticSegmentPage: 'StaticSegmentPage',
    NonCustomerSegmentPage: 'NonCustomerSegmentPage',
    CompositeSegmentPage: 'CompositeSegmentPage',
    SMSTemplatePage: 'SMSTemplatePage',
    HooryTemplatePage: 'HooryTemplatePage',
    TelegramTemplatePage: 'TelegramTemplatePage',
    InternalMessageTemplatePage: 'InternalMessageTemplatePage',
    EmailTemplatePage: 'EmailTemplatePage',
    DynamicPopUpTemplatePage: 'DynamicPopUpTemplatePage',
    CampaignPage: 'CampaignPage',
    CampaignPageStepper: 'CampaignPageStepper',
    JourneyPage: 'JourneyPage',
    ReportPage: 'ReportPage',
    PushNotificationTemplatePage: 'PushNotificationTemplatePage',
    PopUpNotificationTemplatePage: 'PopUpNotificationTemplatePage',
    CMSPopUpNotificationTemplatePage: 'CMSPopUpNotificationTemplatePage',
    CMSBannerTemplatePage: 'CMSBannerTemplatePage',
    ReportExecuteSchedulePage: 'ReportExecuteSchedulePage',
    CompositeReportPage: 'CompositeReportPage',
    CustomerJourneyReportPage: 'CustomerJourneyReportPage',
    ViberTemplatePage: 'ViberTemplatePage',
    WhatsAppTemplatePage: 'WhatsAppTemplatePage',
    WhatsAppProviderTemplatePage: 'WhatsAppProviderTemplatePage',
    ExclusionRulesPage: 'ExclusionRulesPage',
    FormulaFilterPage: 'FormulaFilterPage',
    AchievementPage: 'AchievementPage',
    KnowledgeCenterArticlePage: 'KnowledgeCenterArticlePage',
};

const ReportStatuses = [
    {
        label: 'Scheduled',
        value: '1',
    },
    {
        label: 'Standart',
        value: '0',
    },
];

const ReportType = [
    {
        label: 'CustomReport',
        value: '1',
    },
    {
        label: 'Report',
        value: '0',
    },
    {
        label: 'CompositeReport',
        value: '2',
    },
    {
        label: 'CustomerJourneyReport',
        value: '3',
    },
];

const ReportPageType = {
    Segment: l.Segment,
    Report: l.Report,
    ExclusionRule: l.ExclusionRule,
};

const ReportTypesLabels = {
    0: 'Report',
    1: 'CustomReport',
    2: 'CompositeReport',
    3: 'CustomerJourneyReport',
    Report: 0,
    CustomReport: 1,
    CompositeReport: 2,
    CustomerJourneyReport: 3,
};

const ReportTypeIcons = {
    [ReportTypesLabels.Report]: 'bc-icon-crm-standart-report',
    [ReportTypesLabels.CustomReport]: 'bc-icon-crm-custom-report',
    [ReportTypesLabels.CompositeReport]: 'bc-icon-composite-report',
    [ReportTypesLabels.CustomerJourneyReport]: 'bc-icon-cj-report',
};

const MonthsAndDays = {
    Jan: 31,
    Feb: 29,
    Mar: 31,
    Apr: 30,
    May: 31,
    Jun: 30,
    Jul: 31,
    Aug: 31,
    Sep: 30,
    Oct: 31,
    Nov: 30,
    Dec: 31,
};

const MonthsAndDaysUpperCase = {
    JAN: 31,
    FEB: 29,
    MAR: 31,
    APR: 30,
    MAY: 31,
    JUN: 30,
    JUL: 31,
    AUG: 31,
    SEP: 30,
    OCT: 31,
    NOV: 30,
    DEC: 31,
};

const ReportStateLabels = {
    0: 'Standart',
    1: 'Scheduled',
    Standart: 0,
    Scheduled: 1,
};

const WeekDaysKeyList = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

const Months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

const Days = [
    '1',
    '2',
    '3',
    '4',
    '5',
    '6',
    '7',
    '8',
    '9',
    '10',
    '11',
    '12',
    '13',
    '14',
    '15',
    '16',
    '17',
    '18',
    '19',
    '20',
    '21',
    '22',
    '23',
    '24',
    '25',
    '26',
    '27',
    '28',
    '29',
    '30',
    '31',
];

const Hours = [
    '00',
    '01',
    '02',
    '03',
    '04',
    '05',
    '06',
    '07',
    '08',
    '09',
    '10',
    '11',
    '12',
    '13',
    '14',
    '15',
    '16',
    '17',
    '18',
    '19',
    '20',
    '21',
    '22',
    '23',
];

const ReportsResultStatus = {
    0: 'Done',
    1: 'InProgress',
    2: 'Fail',
    3: 'Stop',
    4: 'NotStarted',
    5: 'Expired',
    Done: 0,
    InProgress: 1,
    Fail: 2,
    Stop: 3,
    NotStarted: 4,
    Expired: 5,
};

const EventLogType = {
    1: 'Error',
    2: 'Warning',
    3: 'Information',
    Error: 1,
    Warning: 2,
    Information: 3,
};

const DynamicBonusTypes = {
    Object: 1,
    KPI: 2,
    FromFile: 3,
    1: 'Object',
    2: 'KPI',
    3: 'FromFile',
} as const;

const KPISources = {
    Object: 'Object',
    KPI: 'KPI',
    FromFile: 'FromFile',
} as const;

const CustomerJourneyGroupBlockTypes = {
    1: 'target',
    2: 'action',
    3: 'trigger',
    5: 'condition',
    target: 'target',
    action: 'action',
    trigger: 'trigger',
    condition: 'condition',
};

const CustomerJourneyGroupBlockTypesLabels = {
    target: l.Target,
    trigger: l.Trigger,
    condition: l.Condition,
    action: l.Action,
};

const CustomerJourneyGroupBlockTypesValues = {
    3: l.Trigger,
    1: l.Target,
    2: l.Action,
    5: l.Condition,
};

const CJModelRelationScopes = {
    None: 0,
    Self: 1,
    Previous: 2,
    All: 3,
} as const;

const CJVisualizationDueDateTypeLabels = {
    [CJVisualizationDueDateTypeEnum.DynamicDate]: l.DynamicDate,
    [CJVisualizationDueDateTypeEnum.Duration]: l.Duration,
};

const CJVisualizationUnlockConditionTypeLabels = {
    [CJVisualizationUnlockConditionTypeEnum.PassSelectedStep]: l.PassSelectedStep,
    [CJVisualizationUnlockConditionTypeEnum.CJCondition]: l.CJCondition,
    [CJVisualizationUnlockConditionTypeEnum.DateTime]: l.DateTime,
};

const CJAutoMapMode = {
    None: 0,
    Closest: 1,
    Single: 2,
};

const CJArgumentValuesTypes = {
    DefaultInArgument: 'DefaultInArgument',
    StaticInArgument: 'StaticInArgument',
    ReferenceInArgument: 'ReferenceInArgument',
    DynamicDateInArgument: 'DynamicDateInArgument',
    FormulaInArgument: 'FormulaInArgument',
};

const CJReferenceInArgumentType =
    'BetConstruct.AGP.CRM.Workflow.Configuration.ReferenceInArgumentConfig, BetConstruct.AGP.CRM.Workflow';

const CJDynamicDateInArgumentType =
    'BetConstruct.AGP.CRM.Workflow.Configuration.DynamicDateInArgumentConfig, BetConstruct.AGP.CRM.Workflow';

const CJFormulaInArgumentType =
    'BetConstruct.AGP.CRM.Workflow.Configuration.FormulaInArgumentConfig, BetConstruct.AGP.CRM.Workflow';

// static (primitive) value wrapped as an InArgument object, e.g. the CJ visualization progress Goal of type Count
const CJStaticInArgumentType =
    'BetConstruct.AGP.CRM.Workflow.Configuration.StaticInArgumentConfig, BetConstruct.AGP.CRM.Workflow';

const defaultLogicFunction = 'none';

const clientIdTypeName = 'WfClientId';
const remainingTimeTypeName = 'WfRemainingTime';

const ExpirationBonusModelTypes = {
    expirationDate: 'BetConstruct.AGP.CRM.Models.Bonus.ExpirationDate, BetConstruct.AGP.CRM.Models',
    expirationPeriod: 'BetConstruct.AGP.CRM.Models.Bonus.ExpirationPeriod, BetConstruct.AGP.CRM.Models',
    'BetConstruct.AGP.CRM.Models.Bonus.ExpirationDate, BetConstruct.AGP.CRM.Models': 'expirationDate',
    'BetConstruct.AGP.CRM.Models.Bonus.ExpirationPeriod, BetConstruct.AGP.CRM.Models': 'expirationPeriod',
};

const BonusModelTypesConfigs = {
    [BonusTypesLabels.WonderWheelSpin]: 'SpinConfiguration',
    [BonusTypesLabels.NftVoucherBonus]: 'NftVoucherBonusConfig',
};

const ExpirationTypes = {
    ExpirationDate: 'expirationDate',
    ExpirationPeriod: 'expirationPeriod',
} as const;

const GoalOptions = {
    0: 'Bet',
    1: 'Deposit',
    Bet: 0,
    Deposit: 1,
};

const DynamicInputTypes = {
    INPUT: 0,
    DROP_DOWN: 1,
    0: 'INPUT',
    1: 'DROP_DOWN',
};

const SegmentObjectType = {
    Client: 0,
    NonCustomer: 1,
    0: 'Client',
    1: 'NonCustomer',
};

const layoutDirections = {
    LR: 'LR',
    TB: 'TB',
};

const ViewModeTypes = {
    0: 'view-mode-small',
    1: 'view-mode-normal',
    2: 'view-mode-large',
};

const ViewThemeTypes = {
    0: 'view-theme-light',
    1: 'view-theme-dark',
};

const Widgets = {
    1: 'ActiveUsers',
    2: 'RetentionRate',
    3: 'ConversionRate',
    28: 'MoneyRetentionRate',
    29: 'DAU_MAU',
    31: 'DAU_WAU',
    32: 'CampaignsInAction',
    33: 'CustomerRelationshipMetrics',
    34: 'DailyMetricsForNewClients',
    35: 'DailyActivityMetricsByProducts',
    37: 'ConversionTimeAnalysis',
    39: 'ARPU',
    40: 'ReactivationRate',
    41: 'CustomerLifetimeValue',
    42: 'DepositClientsCount',
    43: 'LinkOpenClickPerformance',
    ActiveUsers: 1,
    RetentionRate: 2,
    ConversionRate: 3,
    MoneyRetentionRate: 28,
    DAU_MAU: 29,
    DAU_WAU: 31,
    CampaignsInAction: 32,
    CustomerRelationshipMetrics: 33,
    DailyMetricsForNewClients: 34,
    DailyActivityMetricsByProducts: 35,
    ConversionTimeAnalysis: 37,
    ARPU: 39,
    ReactivationRate: 40,
    CustomerLifetimeValue: 41,
    DepositClientsCount: 42,
    LinkOpenClickPerformance: 43,
};

const WidgetsCategory = {
    0: 'KPIs',
    1: 'Charts',
    2: 'Campaigns',
    KPIs: 0,
    Charts: 1,
    Campaigns: 2,
};

const WidgetsCompStatus = {
    initial: 'initial',
    up: 'up',
    down: 'down',
};

const SaveModesLabels = {
    1: SaveModesEnum.SaveAsLive,
    3: SaveModesEnum.SaveAsDraft,
    [SaveModesEnum.SaveAsLive]: 1,
    [SaveModesEnum.SaveAsDraft]: 3,
};

const GenderLabels = {
    0: '',
    1: 'Male',
    2: 'Female',
};

const BonusTypeLabels = {
    2: 'WageringBonus',
    3: 'NoDepositBonus',
    4: 'CorrectionBonus',
    5: 'FreeSpin',
    6: 'FreeBet',
    8: 'SkillGames',
    9: 'BoostBonus',
    10: 'FreeChip',
    12: 'LoyaltyPoint',
    13: 'CashBonus',
    14: 'NFTBonus',
    15: 'FrenchFreeBet',
    17: 'LoyaltyBenefit',
    18: 'WonderWheelSpin',
    19: 'NftVoucherBonus',
};

const BonusAcceptanceTypeLabels = {
    0: 'None',
    1: 'Accepted',
    2: 'Activated',
    3: 'Rejected',
    4: 'Expired',
};

const BonusResultTypeLabels = {
    0: 'None',
    1: 'Paid',
    2: 'Lost',
    3: 'Canceled',
    4: 'Expired',
    6: 'Converted',
};
const ConfigurationStatusLabels = {
    All: '0',
    Live: '1',
    Draft: '2',
    0: 'All',
    1: 'Live',
    2: 'Draft',
};

const ConfigurationEventStatus = [
    {
        label: l.All,
        value: ConfigurationStatusLabels.All,
    },
    {
        label: l.Live,
        value: ConfigurationStatusLabels.Live,
    },
    {
        label: l.Draft,
        value: ConfigurationStatusLabels.Draft,
    },
];

const CustomerJourneyErrorCodes = {
    minimumTwoValidBlocksWithValidConnection: {
        label: l.MinimumTwoValidBlocksWithValidConnection,
        code: 0,
    },
    leastOneTargetBlock: {
        label: l.LeastOneTargetBlock,
        code: 1,
    },
    allBlocksMustBeConnected: {
        label: l.AllBlocksMustBeConnected,
        code: 2,
    },
    targetHasNotInput: {
        label: l.TargetHasNotInput,
        code: 3,
    },
    blockNameCanNotBeEmpty: {
        label: l.BlockNameCanNotBeEmpty,
        code: 4,
    },
    blockInvalid: {
        label: l.BlockInvalid,
        code: 5,
    },
    bonusDetailsGettingError: {
        label: l.BonusDetailsGettingError,
        code: 6,
    },
    unavailableBlockError: {
        label: l.UnavailableBlockError,
        code: 7,
    },
    blockIDMustBeUnique: {
        label: l.BlockIDMustBeUnique,
        code: 8,
    },
    0: 'minimumTwoValidBlocksWithValidConnection',
    1: 'leastOneTargetBlock',
    2: 'allBlocksMustBeConnected',
    3: 'targetHasNotInput',
    4: 'blockNameCanNotBeEmpty',
    5: 'blockInvalid',
    6: 'bonusDetailsGettingError',
    7: 'unavailableBlockError',
    8: 'blockIDMustBeUnique',
};

const DefaultDatesDict = {
    dailyView: {
        day: '',
        days: [],
        isActivePicker: false,
        month: '',
        months: [],
        viewType: 'dailyView',
        weeks: [],
        year: '',
        years: [],
    },
};

const FlowEditorElementsSetterActionTypes = {
    None: 'None',
    RemoveElements: 'RemoveElements',
};

const CJUITypes = {
    DropDown: 1,
    AutoComplete: 2,
    1: 'DropDown',
    2: 'AutoComplete',
};

const CJMenuItemDropDownTypes = {
    Integer: 'Integer',
    String: 'String',
};

const MenuItemTypes = {
    Query: 0,
    Classifier: 1,
    Custom: 2,
    MultiField: 3,
};

const excludeRightValueContentVisibilitiesOperators = ['isempty', 'isnotempty'];

const DownloadFileTypes = {
    xlsx: {
        fileType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        fileExtension: '.xlsx',
    },
    pdf: {
        fileType: 'application/pdf',
        fileExtension: '.pdf',
    },
    csv: {
        fileType: 'text/csv',
        fileExtension: '.csv',
    },
};
const newVersionBuilderConfig = '{"version":"2.0"}';

const timeSpanDefaultValue = '0.00:00:00';

const DownloadPopoverViews = {
    typesView: 'typesView',
    countView: 'countView',
};

const DownloadExtensions = {
    csv: 'csv',
    xlsx: 'xlsx',
};

const partnerIdQueryNamespace = 'partnerId';

const DataTableColumnsCustomTypes = {
    Date: 'Date',
};

const SortDirectionTypes = {
    ASC: 'asc',
    DESC: 'desc',
} as const;

//TODO need translate
const GoalDropdownOptions = [
    { label: l.Bet, value: 0 },
    { label: l.Deposit, value: 1 },
];
const Appearance = {
    Light: 0,
    Dark: 1,
    0: 'Light',
    1: 'Dark',
};

const ViewMode = {
    Small: 0,
    Normal: 1,
    Large: 2,
    0: 'Small',
    1: 'Normal',
    2: 'Large',
};

const CommonIcons = {
    Info: 'bc-icon-info',
};

const ActionsIcons = {
    Delete: 'bc-icon-delete-2',
    Archive: 'bc-icon-archive',
    UnArchive: 'bc-icon-unarchive',
    Start: 'bc-icon-status-play',
    Stop: 'bc-icon-info-48',
    UsedIn: 'bc-icon-info',
    Resume: 'bc-icon-resume',
    Profit: 'bc-icon-profit',
    Clone: 'bc-icon-clone',
    Edit: 'bc-icon-edit',
    TransferBold: 'bc-icon-transfer-bold',
    StatusStop: 'bc-icon-status-stop',
    ViewDoc: 'bc-icon-view-doc',
    Settings: 'bc-icon-settings',
    Rerun: 'bc-icon-crm-rerun',
    Unlink: 'bc-icon-unlink',
    Link: 'bc-icon-link',
    Add: 'bc-icon-add',
    PlusCrm: 'bc-icon-plus-crm',
    Schedule: 'bc-icon-crm-schedule',
    Unschedule: 'bc-icon-crm-unschedule',
    ConvertToReport: 'bc-icon-convert-to-report',
    MessageClosed: 'bc-icon-message-closed',
    SetDefault: 'bc-icon-success-fill',
    Resend: 'bc-icon-resend',
    ViewReport: 'bc-icon-analytic-data-rmt-36',
    Visualization: 'bc-icon-visualization',
    ChangePartnerActivation: 'bc-icon-switcher',
    ManageTag: 'bc-icon-set-tag-block',
    STOPABTest: 'bc-icon-test-stop',
    Predefined: 'bc-icon-predefined',
    Approve: 'bc-icon-success-fill',
    Cancel: 'bc-icon-cancel',
    Reject: 'bc-icon-error-fill',
    Assign: 'bc-icon-user',
    Publish: 'bc-icon-activate-cms',
    Unpublish: 'bc-icon-inactivate',
};

const AuthIcons = {
    Activate: 'bc-icon-activate-cms',
    Inactivate: 'bc-icon-inactivate',
};

const ModalsClassNames = {
    Delete: 'crm-delete-modal',
    Archive: 'crm-archive-modal',
    UnArchive: 'crm-unarchive-modal',
    Start: 'crm-archive-modal',
    Stop: 'crm-stop-modal campaign-stop-modal',
    UsedIn: 'crm-delete-modal',
    Warning: 'crm-warning-modal',
};

const ObjectTypesMap = {
    0: DynamicBonusTypes.KPI,
    9999: DynamicBonusTypes.Object,
    1003: DynamicBonusTypes.FromFile,
    FromFile: 1003,
    Object: 9999,
    KPI: 0,
};

const ObjectTypeLabelMap = {
    0: KPISources.KPI,
    9999: KPISources.Object,
    1003: KPISources.FromFile,
    FromFile: 1003,
    Object: 9999,
    KPI: 0,
};

const BonusTriggerType = {
    Manual: 0, // Amount must be specified
    Auto: 1, // No amount or calculated by provider
    0: 'Manual',
    1: 'Auto',
};

const CampaignLogStatusLabels = {
    0: 'Success',
    1: 'Failed',
    2: 'Pending',
    3: 'Queued',
    4: 'Skipped',
    Success: 0,
    Failed: 1,
    Pending: 2,
    Queued: 3,
    Skipped: 4,
};

const CampaignLogStatusIcons = {
    [CampaignLogStatusLabels.Success]: 'bc-icon-success',
    [CampaignLogStatusLabels.Failed]: 'bc-icon-error',
};

const CascadeRequestTypes = {
    GET: 'GET',
    POST: 'POST',
};

const CascadeUITypes = {
    Text: 0,
    DropDown: 1,
    Number: 5,
    Tree: 17,
    0: 'Text',
    1: 'DropDown',
    5: 'Number',
    17: 'Tree',
};

const ModalActionsConfig = {
    [ModalsActions.ARCHIVE]: {
        dataKey: 'ArchivedDate',
        typeKey: ArchiveUnarchiveTypes.Archive,
    },
    [ModalsActions.UNARCHIVE]: {
        dataKey: 'ArchivedDate',
        typeKey: ArchiveUnarchiveTypes.UnArchive,
    },
    [ModalsActions.DELETE]: {
        independent: true,
    },
    [ModalsActions.START]: {
        dataKey: 'Status',
        typeKey: [CustomerJourneyStatusLabels.InProgress, CustomerJourneyStatusLabels.All],
    },
    [ModalsActions.RESUME]: {
        dataKey: 'Status',
        typeKey: [CustomerJourneyStatusLabels.InProgress, CustomerJourneyStatusLabels.All],
    },
    [ModalsActions.CONVERT_TO_REPORT]: {
        dataKey: 'IsUsed',
        typeKey: EntityStatesLabels.Used,
    },
    [ModalsActions.STOP]: {
        dataKey: 'Status',
        typeKey: [CampaignStatusLabels.Stopped, CampaignStatusLabels.All],
    },
    [ModalsActions.CUSTOMER_JOURNEY_STOP]: {
        dataKey: 'Status',
        typeKey: [CustomerJourneyStatusLabels.Suspended, CustomerJourneyStatusLabels.All],
    },
    [ModalsActions.SCHEDULE_REPORT]: {
        dataKey: 'State',
        typeKey: ReportStateLabels.Scheduled,
    },
    [ModalsActions.UNSCHEDULE_REPORT]: {
        dataKey: 'State',
        typeKey: ReportStateLabels.Standart,
    },
    [ModalsActions.SET_GOAL]: {
        dataKey: 'GoalName',
        typeKey: null, // filter does not exist because typeKey is null
    },
    [ModalsActions.DELETE_GOAL]: {
        dataKey: 'GoalName',
        typeKey: null, // filter does not exist because typeKey is null
    },
};

const CommunicationTypeLabels = {
    1: 'Email',
    2: 'SMS',
    3: 'PushNotification',
    4: 'PopUpNotification',
    5: 'InternalMessage',
    6: 'Telegram',
    7: 'Bonus',
    8: 'CMSPopUpNotification',
    9: 'Viber',
    10: 'Hoory',
    12: 'DynamicPopUp',
    14: 'WhatsApp',
    15: 'CMSBanner',
    16: 'WhatsAppProvider',
    Email: 1,
    SMS: 2,
    PushNotification: 3,
    PopUpNotification: 4,
    InternalMessage: 5,
    Telegram: 6,
    Bonus: 7,
    CMSPopUpNotification: 8,
    Viber: 9,
    Hoory: 10,
    DynamicPopUp: 12,
    WhatsApp: 14,
    CMSBanner: 15,
    WhatsAppProvider: 16,
};

const CommunicationTypeColorsByStatus = {
    [CommunicationTypeLabels.Email]: { success: '#1F77B4', failed: '#9ECAE1' },
    [CommunicationTypeLabels.SMS]: { success: '#FF7F0E', failed: '#FFBB78' },
    [CommunicationTypeLabels.PushNotification]: { success: '#2CA02C', failed: '#98DF8A' },
    [CommunicationTypeLabels.PopUpNotification]: { success: '#D62728', failed: '#FF9896' },
    [CommunicationTypeLabels.InternalMessage]: { success: '#9467BD', failed: '#C5B0D5' },
    [CommunicationTypeLabels.Telegram]: { success: '#17BECF', failed: '#9EDAE5' },
    [CommunicationTypeLabels.Bonus]: { success: '#BCBD22', failed: '#DBDB8D' },
    [CommunicationTypeLabels.CMSPopUpNotification]: { success: '#8C564B', failed: '#C49C94' },
    [CommunicationTypeLabels.Viber]: { success: '#E377C2', failed: '#F7B6D2' },
    [CommunicationTypeLabels.Hoory]: { success: '#7F7F7F', failed: '#C7C7C7' },
    [CommunicationTypeLabels.DynamicPopUp]: { success: '#AEC7E8', failed: '#D6E6F5' },
    [CommunicationTypeLabels.WhatsApp]: { success: '#00CC96', failed: '#66E0C2' },
};

const CommunicationStatusLabels = {
    0: 'Success',
    1: 'Failed',
    2: 'Pending',
    3: 'Queued',
    4: 'Skipped',
    Success: 0,
    Failed: 1,
    Pending: 2,
    Queued: 3,
    Skipped: 4,
};

const CommunicationPromotionTypeLabels = {
    1: 'Campaign',
    2: 'CustomerJourney',
    3: 'NotificationCampaign',
    4: 'Manual',
    5: 'TestSend',
    6: 'ActionCampaign',
    Campaign: 1,
    CustomerJourney: 2,
    NotificationCampaign: 3,
    Manual: 4,
    TestSend: 5,
    ActionCampaign: 6,
};

const BonusStateLabels = {
    0: l.Enabled,
    1: l.Disabled,
    Enabled: 0,
    Disabled: 1,
};

const CommunicationType = [
    {
        label: l.Email,
        value: CommunicationTypeLabels.Email,
    },
    {
        label: l.SMS,
        value: CommunicationTypeLabels.SMS,
    },
    {
        label: l.PushNotification,
        value: CommunicationTypeLabels.PushNotification,
    },
    {
        label: l.PopUpNotification,
        value: CommunicationTypeLabels.PopUpNotification,
    },
    {
        label: l.InternalMessage,
        value: CommunicationTypeLabels.InternalMessage,
    },
    {
        label: l.Telegram,
        value: CommunicationTypeLabels.Telegram,
    },
    {
        label: l.Bonus,
        value: CommunicationTypeLabels.Bonus,
    },
    {
        label: l.CMSPopUpNotification,
        value: CommunicationTypeLabels.CMSPopUpNotification,
    },
    {
        label: l.Viber,
        value: CommunicationTypeLabels.Viber,
    },
    {
        label: l.Hoory,
        value: CommunicationTypeLabels.Hoory,
    },
    {
        label: l.DynamicPopUp,
        value: CommunicationTypeLabels.DynamicPopUp,
    },
    {
        label: l.WhatsApp,
        value: CommunicationTypeLabels.WhatsApp,
    },
    {
        label: l.WhatsAppProvider,
        value: CommunicationTypeLabels.WhatsAppProvider,
    },
];

const CommunicationStatus = [
    {
        label: l.Success,
        value: CommunicationStatusLabels.Success,
    },
    {
        label: l.Failed,
        value: CommunicationStatusLabels.Failed,
    },
    {
        label: l.Pending,
        value: CommunicationStatusLabels.Pending,
    },
    {
        label: l.Skipped,
        value: CommunicationStatusLabels.Skipped,
    },
    {
        label: l.Queued,
        value: CommunicationStatusLabels.Queued,
    },
];

export const ObjectTypeIconsNew = {
    [ObjectTypeEnum.Segment]: 'bc-icon-segment',
    [ObjectTypeEnum.Template]: 'bc-icon-template',
    [ObjectTypeEnum.Campaign]: 'bc-icon-campaign',
    [ObjectTypeEnum.Workflow]: 'bc-icon-customer-journey',
    [ObjectTypeEnum.Report]: 'bc-icon-reports',
};

const CommunicationStatusLabelsMap = {
    [CommunicationStatusLabels.Success]: l.Success,
    [CommunicationStatusLabels.Failed]: l.Failed,
    [CommunicationStatusLabels.Pending]: l.Pending,
    [CommunicationStatusLabels.Skipped]: l.Skipped,
    [CommunicationStatusLabels.Queued]: l.Queued,
};

const CommunicationPromotionType = [
    {
        label: l.Campaign,
        value: CommunicationPromotionTypeLabels.Campaign,
    },
    {
        label: l.CustomerJourney,
        value: CommunicationPromotionTypeLabels.CustomerJourney,
    },
    {
        label: l.NotificationCampaign,
        value: CommunicationPromotionTypeLabels.NotificationCampaign,
    },
    {
        label: l.Manual,
        value: CommunicationPromotionTypeLabels.Manual,
    },
    {
        label: l.TestSend,
        value: CommunicationPromotionTypeLabels.TestSend,
    },
    {
        label: l.ActionCampaign,
        value: CommunicationPromotionTypeLabels.ActionCampaign,
    },
];
const RouteLeaveConfirmationType = {
    Always: 0,
    ChangeRelated: 1,
};

const EventNotifications = {
    BET_WIN_NOTIFICATION: 1,
    WITHDRAW_APPROVAL_NOTIFICATION: 2,
    WITHDRAW_REJECTED_NOTIFICATION: 3,
    MATCH_STARTED_NOTIFICATION: 4,
    MATCH_STARTED_NOTIFICATION_WITH_PRICES: 5,
    MATCH_ABOUT_TO_START_NOTIFICATION: 6,
    MATCH_ABOUT_TO_START_NOTIFICATION_WITH_PRICES: 7,
    MATCH_ENDED_NOTIFICATION: 8,
    FOOTBALL_GOAL_EVENT_NOTIFICATION: 9,
    FOOTBALL_PENALTY_STARTED_EVENT_NOTIFICATION: 10,
    1: 'BET_WIN_NOTIFICATION',
    2: 'WITHDRAW_APPROVAL_NOTIFICATION',
    3: 'WITHDRAW_REJECTED_NOTIFICATION',
    4: 'MATCH_STARTED_NOTIFICATION',
    5: 'MATCH_STARTED_NOTIFICATION_WITH_PRICES',
    6: 'MATCH_ABOUT_TO_START_NOTIFICATION',
    7: 'MATCH_ABOUT_TO_START_NOTIFICATION_WITH_PRICES',
    8: 'MATCH_ENDED_NOTIFICATION',
    9: 'FOOTBALL_GOAL_EVENT_NOTIFICATION',
    10: 'FOOTBALL_PENALTY_STARTED_EVENT_NOTIFICATION',
};

const NotificationFields = {
    ProviderId: 'ProviderId',
    MessageId: 'MessageId',
    IsEnabled: 'IsEnabled',
};
const LogicTypesValues = { and: 'and', or: 'or' };

const FilterGroupLimit = 1;

const EmailProviderTypeLabels = {
    0: 'SMTP',
    1: 'UniSender',
    2: 'HttpAPI',
    3: 'MailChimp',
    4: 'MailUp',
    SMTP: 0,
    UniSender: 1,
    HttpAPI: 2,
    MailChimp: 3,
    MailUp: 4,
};

const EmailProviderTypes = [
    {
        label: 'SMTP',
        value: '0',
    },
    {
        label: 'UniSender',
        value: '1',
    },
    {
        label: 'HttpAPI',
        value: '2',
    },
    {
        label: 'MailChimp',
        value: '3',
    },
    {
        label: 'MailUp',
        value: '4',
    },
];

const NavigationActionTypes = {
    PUSH: 'PUSH',
    POP: 'POP',
    REPLACE: 'REPLACE',
};

const Position = {
    Top: 'Top',
    Right: 'Right',
    Bottom: 'Bottom',
    Left: 'Left',
    Auto: 'Auto',
};

const ErrorMessageSymbolsCount = 120;

// Largest delay setTimeout accepts (2^31 - 1 ms, ~24.8 days); a bigger value fires immediately.
// Used to keep a toaster open so its own timer decides when to close.
const MaxTimeoutDelay = 2 ** 31 - 1;

const NotificationsDescriptionsLabels = {
    1: l.BetWinDescription,
    2: l.WithdrawAproveDescription,
    3: l.WithdrawRejectedDescription,
    4: l.MatchStartedDescription,
    5: l.MatchStartedWithPricesDescription,
    6: l.MatchAboutStartDescription,
    7: l.MatchAboutStartWithPricesDescription,
    8: l.MatchEndedDescription,
    9: l.FootballGoalDescription,
    10: l.FootballPenaltyStartedDescription,
    11: l.WithdrawalDescription,
    12: l.DepositDescription,
};

const DefaultLangId = 'en';

const PromotionTypesLabels = {
    Campaign: 1,
    CustomerJourney: 2,
    NotificationCampaign: 3,
    ActionCampaign: 6,
    1: l.Campaign,
    2: l.CustomerJourney,
    3: l.NotificationCampaign,
    6: l.ActionCampaign,
};

const NotificationCampaignTypesLabels = {
    0: l.Enabled,
    1: l.Disabled,
    Enabled: 0,
    Disabled: 1,
};

const TemplateInputTypes = {
    TitleOfPushNotification: 'TitleOfPushNotification',
    Subject: 'Subject',
    Category: 'TemplateCategoryIds',
    Body: 'Body',
    SMSBody: 'SMSBody',
    InlineKeyboard: 'InlineKeyboard',
    InlineKeyboardFormikValidity: 'InlineKeyboardFormikValidity',
    ButtonText: 'ButtonText',
    ButtonUrl: 'ButtonUrl',
    Url: 'Url',
    UrlType: 'UrlType',
    DeviceType: 'DeviceType',
    UploadedImageUrl: 'UploadedImageUrl',
    FileName: 'FileName',
    ImageURLPlaceholderDropdown: 'ImageURLPlaceholderDropdown',
    ValidityPeriod: 'ValidityPeriod',
    SectionUrl: 'SectionUrl',
    NotificationCategory: 'NotificationCategory',
    IsImportant: 'IsImportant',
    ExternalWhatsAppTemplateLanguage: 'ExternalWhatsAppTemplateLanguage',
    ExternalWhatsAppTemplateId: 'ExternalWhatsAppTemplateId',
    ExternalWhatsAppTemplateDetails: 'ExternalWhatsAppTemplateDetails',
    ExternalWhatsAppTemplatePlaceholderMapping: 'ExternalWhatsAppTemplatePlaceholderMapping',
    ExpirationType: 'ExpirationType',
    ExpirationPeriod: 'ExpirationPeriod',
    ExpirationDate: 'ExpirationDate',
    ExpirationTime: 'ExpirationTime',
    Timezone: 'Timezone',
};

const CommunicationAvailabilityScheduleTypeLabels = {
    0: 'Always',
    1: 'SameEveryDay',
    2: 'WeekdaysAndWeekend',
    7: 'DaysOfTheWeek',
    Always: 0,
    SameEveryDay: 1,
    WeekdaysAndWeekend: 2,
    DaysOfTheWeek: 7,
};

const CommunicationAvailabilityScheduleTypes = [
    { label: l.CommunicationAvailabilityAlways, value: 0 },
    { label: l.CommunicationAvailabilitySameEveryDay, value: 1 },
    { label: l.CommunicationAvailabilityWeekdaysAndWeekend, value: 2 },
    { label: l.CommunicationAvailabilityDaysOfTheWeek, value: 7 },
];
const DataTablePagingOptions = [
    {
        label: '20',
        value: 20,
        defaultSelected: true,
    },
    {
        label: '30',
        value: 30,
    },
    {
        label: '100',
        value: 100,
    },
];

const CampaignActivityDefaultPageSize = 10;

const ExecutedReportParametersDataTypes = {
    1: 'Date',
    2: 'Int',
    3: 'String',
    4: 'MenuItem',
    Date: 1,
    Int: 2,
    String: 3,
    MenuItem: 4,
};

const DeviceTabInfos: DeviceTabInfosType = {
    [DeviceTypeEnum.Desktop]: {
        type: DeviceTypeEnum.Desktop,
        titleKey: l.Desktop,
        iconType: 'bc-icon-desktop',
    },
    [DeviceTypeEnum.Mobile]: {
        type: DeviceTypeEnum.Mobile,
        titleKey: l.Mobile,
        iconType: 'bc-icon-mobile',
    },
    [DeviceTypeEnum.Tablet]: {
        type: DeviceTypeEnum.Tablet,
        titleKey: l.Tablet,
        iconType: 'bc-icon-tablet',
    },
};

const ModeTabInfos = {
    [DisplayModeEnum.LightMode]: {
        type: DisplayModeEnum.LightMode,
        titleKey: l.Light,
        iconType: 'bc-icon-light-mode',
    },
    [DisplayModeEnum.DarkMode]: {
        type: DisplayModeEnum.DarkMode,
        titleKey: l.Dark,
        iconType: 'bc-icon-dark-mode',
    },
};

const CJDependencyTypes = {
    Bonus: 'Bonus',
    Provider: 'Provider',
    Template: 'Template',
    KpiConfig: 'KpiConfig',
    TemplateBestChannelTime: 'TemplateBestChannelTime',
};

const CompositeReportOperation = {
    1: 'Union',
    2: 'Intersect',
    3: 'FirstPriority',
    Union: 1,
    Intersect: 2,
    FirstPriority: 3,
};

const ClientsCountType = {
    Target: 0,
    Total: 1,
};

const BooleanTypeLabels = {
    true: l.Yes,
    false: l.No,
};

const PartnerSettingsBaseKindTypes = {
    0: 'Application',
    1: 'Partner',
    3: 'PartnerGroup',
    null: 'Hierarchical',
    Application: 0,
    Partner: 1,
    Hierarchical: null,
    PartnerGroup: 3,
};

const PartnerKindsRadioGroupOptions = [
    {
        label: l.Partner,
        value: 1,
    },
    {
        label: l.PartnerGroup,
        value: 3,
    },
];

const PartnerSettingsValueTypes = {
    0: 'Text',
    1: 'Number',
    2: 'CustomList',
    3: 'JSON',
    Text: 0,
    Number: 1,
    CustomList: 2,
    JSON: 3,
};

const PartnerStatusLabels = {
    0: 'Unknown',
    1: 'Trial',
    2: 'Production',
    3: 'Inactive',
    4: 'Lost',
    5: 'Terminated',
    6: 'Deleted',
    Unknown: '0',
    Trial: '1',
    Production: '2',
    Inactive: '3',
    Lost: '4',
    Terminated: '5',
    Deleted: '6',
};

const PartnerStatus = [
    {
        label: l.Unknown,
        value: PartnerStatusLabels.Unknown,
    },
    {
        label: l.Trial,
        value: PartnerStatusLabels.Trial,
    },
    {
        label: l.Production,
        value: PartnerStatusLabels.Production,
    },
    {
        label: l.Inactive,
        value: PartnerStatusLabels.Inactive,
    },
    {
        label: l.Lost,
        value: PartnerStatusLabels.Lost,
    },
    {
        label: l.Terminated,
        value: PartnerStatusLabels.Terminated,
    },
    {
        label: l.Deleted,
        value: PartnerStatusLabels.Deleted,
    },
];

const SortOrderIDOptions = [
    {
        label: l.Ascending,
        value: SortOrderEnum.ASC,
    },
    {
        label: l.Descending,
        value: SortOrderEnum.DESC,
    },
];

const DependencyObjectTypeLabels = {
    0: 'Campaign',
    1: 'Workflow',
    2: 'Template',
    3: 'Segment',
    4: 'EmailProvider',
    5: 'AdHocReport',
    6: 'SmsProvider',
    7: 'CompositeReport',
    8: 'WorkflowReport',
    Campaign: 0,
    Workflow: 1,
    Template: 2,
    Segment: 3,
    EmailProvider: 4,
    AdHocReport: 5,
    SmsProvider: 6,
    CompositeReport: 7,
    WorkflowReport: 8,
};

const FieldTypes = {
    CustomField: 'customField',
};

const GlobalEvents = {
    PartnerChanged: 'PartnerChanged',
    UserSettingsChange: 'UserSettingsChange',
    ClientTagsChange: 'ClientTagsChange',
    LanguageChanged: 'LanguageChanged',
    RouteLeaveConfirmed: 'RouteLeaveConfirmed',
} as const;

const SubscriberSources = {
    MenuItem: 'MenuItem',
    PartnerLanguage: 'PartnerLanguage',
    QueryConfiguration: 'QueryConfiguration',
    ExclusionRulesQueryConfiguration: 'ExclusionRulesQueryConfiguration',
    UserInfo: 'UserInfo',
    ReportColumn: 'ReportColumn',
    ClientInfoPreview: 'ClientInfoPreview',
    PartnerCurrencies: 'PartnerCurrencies',
    DynamicSegmentQueryConfiguration: 'DynamicSegmentQueryConfiguration',
    ReportQueryConfiguration: 'ReportQueryConfiguration',
    CustomerJourneyDraft: 'CustomerJourneyDraft',
};

const SEGMENT_FILTER_APPLICATION_COLUMN_ID = 5147;

const DangerConfidencePercentage = 95;

const ClientColumnSets = {
    previewableColumns: (columns) =>
        columns
            .filter((item) => !isNil(item.PreviewColumnOrder))
            .sort((a, b) => a.PreviewColumnOrder - b.PreviewColumnOrder),
    sortableColumns: (columns) => columns.filter((item) => item.IsSortable),
};

const ApplicationColumnIds = {
    Client: 38,
    MainCurrency: 40,
    KPICurrency: 7492,
};

const RealtimePromotionTypes = {
    ActionCampaign: '6',
    CustomerJourney: '2',
    6: 'ActionCampaign',
    2: 'CustomerJourney',
};

const RealtimePromotionTypeIcons = {
    [RealtimePromotionTypes.CustomerJourney]: 'bc-icon-customer-journey',
    [RealtimePromotionTypes.ActionCampaign]: 'bc-icon-action-compaign',
};

const PromotionTypesDropdownData = [
    { label: l.ActionCampaign, value: RealtimePromotionTypes.ActionCampaign },
    { label: l.CustomerJourney, value: RealtimePromotionTypes.CustomerJourney },
];

const CustomerJourneyInfoModalTitleLabels = {
    [RealtimePromotionTypes.CustomerJourney]: 'CJInfo',
    [RealtimePromotionTypes.ActionCampaign]: 'ActionCampaignInfo',
};

const SupportedComparisonFilterFieldsTypes = {
    0: FilterHolderFields.textInput.type,
    1: FilterHolderFields.select.type,
    5: FilterHolderFields.textInput.type,
    14: FilterHolderFields.menuItemMultiSelect.type,
};

const ManageTagActionTypes = {
    SetTag: '1',
    RemoveTag: '2',
    SetOnlyForSelection: '3',
    1: l.SetTag,
    2: l.RemoveTag,
    3: l.SetOnlyForSelection,
};

const GACategoryEvents = {
    GACreateCategory: {
        name: 'Create',
        events: {
            // navigation group
            navigationCreateButton: 'navigation_create_button',

            navigationSegmentGroup: 'navigation_create_segment_group',
            navigationTemplateGroup: 'navigation_create_template_group',
            navigationRealtimePromotionGroup: 'navigation_create_realtime_promotion_group',
            navigationReportGroup: 'navigation_create_report_group',
            navigationCampaignGroup: 'navigation_create_campaign',

            navigationDynamicSegment: 'navigation_create_dynamic_segment',
            navigationStaticSegment: 'navigation_create_static_segment',
            navigationCompositeSegment: 'navigation_create_composite_segment',
            navigationNonCustomerSegment: 'navigation_create_non_customer_segment',

            navigationEmailTemplate: 'navigation_create_email_template',
            navigationSMSTemplate: 'navigation_create_sms_template',
            navigationInternalMessageTemplate: 'navigation_create_internal_message_template',
            navigationSocialTemplate: 'navigation_create_social_template',
            navigationPushTemplate: 'navigation_create_push_template',
            navigationPopUpTemplate: 'navigation_create_pop_up_template',
            navigationDynamicPopUpTemplate: 'navigation_create_dynamic_pop_up_template',
            navigationCMSPopUpTemplate: 'navigation_create_cms_pop_up_template',
            navigationCMSBannerTemplate: 'navigation_create_cms_banner_template',
            navigationTelegramTemplate: 'navigation_create_telegram_template',
            navigationViberTemplate: 'navigation_create_viber_template',
            navigationWhatsAppTemplate: 'navigation_create_whats_app_template',
            navigationWhatsAppProviderTemplate: 'navigation_create_whats_app_provider_template',
            navigationHooryTemplate: 'navigation_create_hoory_template',

            navigationJourneyPromotion: 'navigation_create_realtime_journey_promotion',
            navigationRealtimeActionCampaignPromotion: 'navigation_create_realtime_action_campaign_promotion',

            navigationReport: 'navigation_create_report',
            navigationCompositeReport: 'navigation_create_composite_report',
            navigationCustomerJourneyReport: 'navigation_create_customer_journey_report',

            // predefined page group, keys keep the Group suffix because they are looked up by menu suffix
            pageSegmentGroup: 'page_create_predefined_segment',
            pageReportGroup: 'page_create_predefined_report',
            pageTemplateGroup: 'page_create_predefined_template_group',
            pageRealtimePromotionGroup: 'page_create_predefined_realtime_promotion_group',

            // page group
            pageSegmentCreateButton: 'page_segment_create_button',

            pageDynamicSegment: 'page_create_dynamic_segment',
            pageStaticSegment: 'page_create_static_segment',
            pageCompositeSegment: 'page_create_composite_segment',
            pageNonCustomerSegment: 'page_create_non_customer_segment',

            pageTemplateCreateButton: 'page_template_create_button',

            pageEmailTemplate: 'page_create_email_template',
            pageSMSTemplate: 'page_create_sms_template',
            pageInternalMessageTemplate: 'page_create_internal_message_template',
            pageSocialTemplate: 'page_create_social_template',
            pagePushTemplate: 'page_create_push_template',
            pagePopUpTemplate: 'page_create_pop_up_template',
            pageDynamicPopUpTemplate: 'page_create_dynamic_pop_up_template',
            pageCMSPopUpTemplate: 'page_create_cms_pop_up_template',
            pageCMSBannerTemplate: 'page_create_cms_banner_template',
            pageTelegramTemplate: 'page_create_telegram_template',
            pageViberTemplate: 'page_create_viber_template',
            pageWhatsAppTemplate: 'page_create_whats_app_template',
            pageWhatsAppProviderTemplate: 'page_create_whats_app_provider_template',
            pageHooryTemplate: 'page_create_hoory_template',

            pageCampaignCreateButton: 'page_create_campaign',

            pageJourneyCreateButton: 'page_create_journey',

            pageJourneyPromotion: 'page_create_realtime_journey_promotion',
            pageRealtimeActionCampaignPromotion: 'page_create_realtime_action_campaign_promotion',

            pageReportCreateButton: 'page_create_report',

            pageReport: 'page_create_report',
            pageCompositeReport: 'page_create_composite_report',
            pageCustomerJourneyReport: 'page_create_customer_journey_report',
        },
        labels: {
            navigationCreateButton: 'Open create popup',
            pageSegmentCreateButton: 'Segment page create',
            pageTemplateCreateButton: 'Template page create',
            pageCampaignCreateButton: 'Campaign page create',
            pageJourneyCreateButton: 'Journey page create',
            pageReportCreateButton: 'Report page create',
        },
    },
    GASearchCategory: {
        name: 'Search',
        events: {
            globalSearch: 'search',
            filterByObjectType: 'search_filter_object_type',
            filterByUser: 'search_filter_user',
            resultView: 'search_result_view',
            resultInfo: 'search_result_info',
        },
        labels: {
            globalSearch: 'text input',
            filterByUser: 'user filter',
            filterByObjectTypeEmpty: 'none',
        },
    },
    GADetailsCategory: {
        name: 'Details',
        events: {
            campaignInfoActionButton: 'campaign_info_action_button',
            campaignReportFromTitle: 'campaign_report_from_title',
            campaignReportActionButton: 'campaign_report_action_button',
            journeyReportActionButton: 'journey_report_action_button',
            CampaignTitleDetails: 'campaign_details_from_name',
        },
        labels: {
            campaignInfoActionButton: 'campaign info action button',
            campaignReportActionButton: 'campaign report action button',
            journeyReportActionButton: 'journey report action button',
            titleDetails: 'open details from name',
        },
    },
    GAFiltersCategory: {
        name: 'Filters',
        events: {
            filtersTagGroupSegmentListAddButton: 'segment_add_filter_button',
            filtersTagGroupCampaignListAddButton: 'campaign_add_filter_button',
            filtersTagGroupTemplateListAddButton: 'template_add_filter_button',
            filtersTagGroupJourneyPageAddButton: 'journey_add_filter_button',
            filtersTagGroupReportListAddButton: 'report_add_filter_button',

            filtersTagGroupSegmentListLeftSide: 'segment_add_filter_left_side',
            filtersTagGroupCampaignListLeftSide: 'campaign_add_filter_left_side',
            filtersTagGroupTemplateListLeftSide: 'template_add_filter_left_side',
            filtersTagGroupJourneyPageLeftSide: 'journey_add_filter_left_side',
            filtersTagGroupReportListLeftSide: 'report_add_filter_left_side',

            filtersTagGroupSegmentListSaveButton: 'segment_save_filter_button',
            filtersTagGroupCampaignListSaveButton: 'campaign_save_filter_button',
            filtersTagGroupTemplateListSaveButton: 'template_save_filter_button',
            filtersTagGroupJourneyPageSaveButton: 'journey_save_filter_button',
            filtersTagGroupReportListSaveButton: 'report_save_filter_button',

            filtersTagGroupSegmentListSavedButton: 'segment_saved_filters_button',
            filtersTagGroupCampaignListSavedButton: 'campaign_saved_filters_button',
            filtersTagGroupTemplateListSavedButton: 'template_saved_filters_button',
            filtersTagGroupJourneyPageSavedButton: 'journey_saved_filters_button',
            filtersTagGroupReportListSavedButton: 'report_saved_filters_button',

            dataTableTemplateListColumn: 'choose_template_table_columns',
            dataTableSegmentListColumn: 'choose_segment_table_columns',
            dataTableJourneyPageColumn: 'choose_journey_table_columns',
            dataTableCampaignListColumn: 'choose_campaign_table_columns',
            dataTableReportListColumn: 'choose_report_table_columns',

            createSegmentLabel: 'create_segment_label',
            createTemplateLabel: 'create_template_label',
            createCampaignLabel: 'create_campaign_label',
            createWorkflowLabel: 'create_workflow_label',
            createReportLabel: 'create_report_label',

            communicationListAll: 'show_all_communications',
            campaignPageCommunicationListAll: 'show_all_campaign_communications',
        },
        labels: {
            filtersTagGroupSegmentListLeftSide: 'segment filter icon',
            filtersTagGroupCampaignListLeftSide: 'campaign filter icon',
            filtersTagGroupTemplateListLeftSide: 'template filter icon',
            filtersTagGroupJourneyPageLeftSide: 'journey filter icon',
            filtersTagGroupReportListLeftSide: 'report filter icon',
            createLabel: 'create label',
            communicationListAll: 'communication report page',
        },
    },
    GADateFilterCategory: {
        name: 'DateFilter',
        events: {
            generalDateChangeDate: 'general_date_change',
            DKPIActiveUsersDate: 'widget_active_users_date_change',
            DKPIRetentionRateDate: 'widget_retention_rate_date_change',
            DKPIConversionRateDate: 'widget_conversion_rate_date_change',
            DKPIMoneyRetentionRateDate: 'widget_money_retention_rate_date_change',
            DKPIDAUMAUDate: 'widget_dau_mau_date_change',
            DKPIDAUWAUDate: 'widget_dau_wau_date_change',
            DKPIARPUDate: 'widget_arpu_date_change',
            DKPIReactivationRateDate: 'widget_reactivation_rate_date_change',
            DKPICLVDate: 'widget_clv_date_change',

            inActionPromotionYesterday: 'in_action_promotion_yesterday_date_change',
            inActionPromotionToday: 'in_action_promotion_today_date_change',
            inActionPromotionTomorrow: 'in_action_promotion_tomorrow_date_change',

            customerRelationship: 'customer_relationship_metrics_date_change',
            newCustomersDailyMetrics: 'new_customers_daily_metrics_date_change',
            activeCustomersByProductCategories: 'active_customers_by_product_categories_date_change',
            conversionTimeAnalytics: 'conversion_time_analytics_date_change',
            dailyCommunicationMetrics: 'daily_communication_metrics_date_change',
        },
        labels: {
            generalDateChangeDate: 'general date change',
            kpiWidgetDate: 'change widget date',
            inActionPromotion: 'in action promotion date change',
            customerRelationship: 'relationship metrics date change',
            newCustomersDailyMetrics: 'new customers metrics date',
            activeCustomersByProductCategories: 'active customers by product categories metrics',
            conversionTimeAnalytics: 'conversion time analytics',
            dailyCommunicationMetrics: 'daily communication metrics',
        },
    },
    GAGenericCategory: {
        name: 'Generic',
        events: {
            copyPartnerId: 'copy_partner_id',
        },
    },
    GANavigationCategory: {
        name: 'Navigation',
        events: {
            menuHomePage: 'navigation_menu_home_page',
            menuDashboard: 'navigation_menu_dashboard',
            menuSegments: 'navigation_menu_segments',
            menuTemplates: 'navigation_menu_templates',
            menuCampaigns: 'navigation_menu_campaigns',
            menuRealtimePromotion: 'navigation_menu_realtime_promotion',
            menuReports: 'navigation_menu_reports',
            menuClients: 'navigation_menu_clients',
            menuCommunications: 'navigation_menu_communications',
            menuConfiguration: 'navigation_menu_configuration',
            menuAdministration: 'navigation_menu_administration',
            menuPromotionTimeline: 'navigation_menu_promotion_timeline',
            menuAISegmentation: 'navigation_menu_ai_segmentation',
            menuPredefined: 'navigation_menu_predefined',
            menuObjectApproval: 'navigation_menu_object_approval',
        },
    },
    GAHomePageCategory: {
        name: 'HomePage',
        events: {
            widgetDashboard: 'homepage_widget_dashboard',
            widgetSegments: 'homepage_widget_segments',
            widgetTemplates: 'homepage_widget_templates',
            widgetCampaigns: 'homepage_widget_campaigns',
            widgetRealtimePromotion: 'homepage_widget_realtime_promotion',
            widgetReports: 'homepage_widget_reports',
            widgetClients: 'homepage_widget_clients',
            widgetCommunications: 'homepage_widget_communications',
            widgetConfiguration: 'homepage_widget_configuration',
            widgetAdministration: 'homepage_widget_administration',
            widgetPredefined: 'homepage_widget_predefined',
        },
    },
    GAConfigurationCategory: {
        name: 'Configuration',
        events: {
            widgetGeneral: 'configuration_widget_general',
            widgetNotifications: 'configuration_widget_notifications',
            widgetProviders: 'configuration_widget_providers',
            widgetAwards: 'configuration_widget_awards',
            widgetTemplateLanguages: 'configuration_widget_template_languages',
            widgetLinkManagement: 'configuration_widget_link_management',
            widgetExclusionRules: 'configuration_widget_exclusion_rules',
            widgetFormulaFilters: 'configuration_widget_formula_filters',
            widgetAchievements: 'configuration_widget_achievements',
        },
    },
    GAUserSettingsCategory: {
        name: 'UserSettings',
        events: {
            changeLanguage: 'user_settings_change_language',
            changeCurrency: 'user_settings_change_currency',
            changeAppearance: 'user_settings_change_appearance',
            changeViewMode: 'user_settings_change_view_mode',
        },
    },
    GAPredefinedCategory: {
        name: 'Predefined',
        events: {
            navigateList: 'predefined_list',
            navigateSegmentList: 'predefined_segment_list',
            navigateTemplateList: 'predefined_template_list',
            navigateReportList: 'predefined_report_list',
            navigateCustomerJourneyList: 'predefined_customer_journey_list',
        },
    },
};

const ManageTagActionList = [
    {
        label: l.SetTag,
        value: ManageTagActionTypes.SetTag,
        icon: 'bc-icon-plus-crm',
    },
    {
        label: l.RemoveTag,
        value: ManageTagActionTypes.RemoveTag,
        icon: 'bc-icon-close',
    },
    {
        label: l.SetOnlyForSelection,
        value: ManageTagActionTypes.SetOnlyForSelection,
        icon: 'bc-icon-partner',
    },
];

const ClientKPIColumnTypes = {
    None: 0,
    Aggregate: 1 << 0,
    Currency: 1 << 1,
    Count: 1 << 2,
    Computed: 1 << 3,
    Formula: 1 << 5,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    get ComputedCurrency() {
        return this.Computed | this.Currency;
    },
    // eslint-disable-next-line @typescript-eslint/naming-convention
    get AggregatedCurrency() {
        return this.Aggregate | this.Currency;
    },
    // eslint-disable-next-line @typescript-eslint/naming-convention
    get FormulaCurrency() {
        return this.Formula | this.Currency;
    },
};

const DeniedTags = {
    dynamicPopUp: [],
    email: ['script', 'form', 'input', 'select', 'textarea', 'button'],
    internal: [
        'html',
        'body',
        'head',
        'script',
        'meta',
        'base',
        'title',
        'form',
        'input',
        'select',
        'textarea',
        'button',
    ],
};

const TemplateEditorTypes = {
    HtmlCodeEditor: 'HtmlCodeEditor',
    JoditEditor: 'TextEditor',
    BlockEditor: 'BlockEditor',
    MosaicoEditor: 'BlockEditorLegacy',
};

const TemplateEditorModes = {
    Internal: [
        {
            label: l.BlockEditorLegacy,
            value: TemplateEditorTypes.MosaicoEditor,
        },
        {
            label: l.BlockEditor,
            value: TemplateEditorTypes.BlockEditor,
        },
        {
            label: l.TextEditor,
            value: TemplateEditorTypes.JoditEditor,
        },
        {
            label: l.HtmlCodeEditor,
            value: TemplateEditorTypes.HtmlCodeEditor,
        },
    ],
    Email: [
        {
            label: l.BlockEditorLegacy,
            value: TemplateEditorTypes.MosaicoEditor,
        },
        {
            label: l.BlockEditor,
            value: TemplateEditorTypes.BlockEditor,
        },
        {
            label: l.TextEditor,
            value: TemplateEditorTypes.JoditEditor,
        },
        {
            label: l.HtmlCodeEditor,
            value: TemplateEditorTypes.HtmlCodeEditor,
        },
    ],
    DynamicPopUp: [
        {
            label: l.BlockEditorLegacy,
            value: TemplateEditorTypes.MosaicoEditor,
        },
        {
            label: l.BlockEditor,
            value: TemplateEditorTypes.BlockEditor,
        },
        {
            label: l.TextEditor,
            value: TemplateEditorTypes.JoditEditor,
        },
        {
            label: l.HtmlCodeEditor,
            value: TemplateEditorTypes.HtmlCodeEditor,
        },
    ],
};

const PushNotificationProviderKeys = {
    FireBase: 2,
    OneSignal: 3,
} as const;

const AuthProvidersTypes = {
    BetConstruct: 1,
    Dream: 2,
    Password: 3,
};

const PromotionTypesIcons = {
    [PromotionTypeLabels.Campaign]: 'bc-icon-campaign', //campaign
    [PromotionTypeLabels.CustomerJourney]: 'bc-icon-customer-journey', //customerjourney
    [PromotionTypeLabels.ActionCampaign]: 'bc-icon-action-compaign', // actionCampaign
};

const BonusTriggerTypeEnum = {
    Manual: 0,
    Auto: 1,
    0: 'Manual',
    1: 'Auto',
} as const;

const HexOpacityCodes = {
    0: '00',
    1: '03',
    2: '05',
    3: '08',
    4: '0A',
    5: '0D',
    6: '0F',
    7: '12',
    8: '14',
    9: '17',
    10: '1A',
    11: '1C',
    12: '1F',
    13: '21',
    14: '24',
    15: '26',
    16: '29',
    17: '2B',
    18: '2E',
    19: '30',
    20: '33',
    21: '36',
    22: '38',
    23: '3B',
    24: '3D',
    25: '40',
    26: '42',
    27: '45',
    28: '47',
    29: '4A',
    30: '4D',
    31: '4F',
    32: '52',
    33: '54',
    34: '57',
    35: '59',
    36: '5C',
    37: '5E',
    38: '61',
    39: '63',
    40: '66',
    41: '69',
    42: '6B',
    43: '6E',
    44: '70',
    45: '73',
    46: '75',
    47: '78',
    48: '7A',
    49: '7D',
    50: '80',
    51: '82',
    52: '85',
    53: '87',
    54: '8A',
    55: '8C',
    56: '8F',
    57: '91',
    58: '94',
    59: '96',
    60: '99',
    61: '9C',
    62: '9E',
    63: 'A1',
    64: 'A3',
    65: 'A6',
    66: 'A8',
    67: 'AB',
    68: 'AD',
    69: 'B0',
    70: 'B3',
    71: 'B5',
    72: 'B8',
    73: 'BA',
    74: 'BD',
    75: 'BF',
    76: 'C2',
    77: 'C4',
    78: 'C7',
    79: 'C9',
    80: 'CC',
    81: 'CF',
    82: 'D1',
    83: 'D4',
    84: 'D6',
    85: 'D9',
    86: 'DB',
    87: 'DE',
    88: 'E0',
    89: 'E3',
    90: 'E6',
    91: 'E8',
    92: 'EB',
    93: 'ED',
    94: 'F0',
    95: 'F2',
    96: 'F5',
    97: 'F7',
    98: 'FA',
    99: 'FC',
    100: 'FF',
};

const RecurrenceDataColumnsConfig = [
    {
        columnName: 'SelectedTimeZoneTime',
        columnLabel: l.SelectedTimeZoneTime,
    },
    {
        columnName: 'FiltersTimeZoneTime',
        columnLabel: l.FiltersTimeZoneTime,
    },
];

const LOYALTY_POINT_CURRENCY = 'LP';

const RADIO_SELECT_COLUMN_CONFIG: Partial<DataTableColumn> = {
    sizeParams: {
        defaultCustomWidth: 50,
    },
};

const MULTI_SELECT_COLUMN_CONFIG: Partial<DataTableColumn> = {
    sizeParams: {
        defaultCustomWidth: 50,
    },
};

export const JSON_OBJECT = '{}';

const BLOCKED_BY_SUBSCRIPTION_CLASSNAME = 'crm-blocked-by-subscription';

export const ID_KEY_FOR_TESTING = 'data-tt-id';

export const PredefinedObjectTypesLabelValue = [
    {
        label: l.RealtimePromotion,
        value: String(ObjectTypeEnum.Workflow),
    },
    {
        label: l.Template,
        value: String(ObjectTypeEnum.Template),
    },
    {
        label: l.Segment,
        value: String(ObjectTypeEnum.Segment),
    },
    {
        label: l.Report,
        value: String(ObjectTypeEnum.Report),
    },
];

export {
    l,
    DownloadFileTypes,
    PARTNER_SETTINGS,
    ViewModeTypes,
    ViewThemeTypes,
    FilterHolderFields,
    SegmentTypes,
    SegmentTypesLabels,
    SegmentTypeIcons,
    SegmentTypeIconClasses,
    EntityStates,
    EntityStatesLabels,
    AlertTypes,
    DefinitionTypes,
    ModalsActions,
    StateModesLabels,
    StateModes,
    FullStateModes,
    FilterComparisons,
    LabelsModalViews,
    TagsModalViews,
    CustomersTypes,
    BASE_DOMAIN_PLACEHOLDER,
    TemplateTypesLabels,
    TemplateTypeIcons,
    CommunicationTypeColorsByStatus,
    TargetTypeEnum,
    ListRequestDefaultParams,
    CampaignType,
    CampaignStatus,
    ApprovalRequestPriorityLabels,
    CampaignStatusLabels,
    PageValidationTypes,
    ErrorShowType,
    ReportStatuses,
    ReportType,
    ReportTypesLabels,
    ReportTypeIcons,
    ReportStateLabels,
    CompositeSegmentOperation,
    ScheduleModalViews,
    RepeatingScheduleLabels,
    Hours,
    Days,
    Months,
    WeekDaysKeyList,
    Position,
    ReportsResultStatus,
    EventLogType,
    BonusTypes,
    BonusTypesLabels,
    BonusTypesIcons,
    DynamicBonusTypes,
    PromoConditionLabels,
    PromotionStatusColors,
    CustomerJourneyStatus,
    CustomerJourneyStatusLabels,
    CustomerJourneyWorkingState,
    MonthsAndDays,
    CustomerJourneyGroupBlockTypes,
    CustomerJourneyGroupBlockTypesLabels,
    TimeZonePack,
    ExportFileFormat,
    GoalOptions,
    DynamicInputTypes,
    SegmentObjectType,
    layoutDirections,
    Widgets,
    FieldTypes,
    WidgetsCategory,
    SaveModesLabels,
    DateFormat,
    DateTimeFormat,
    TimeFormat,
    DateTimeWithoutSecondsFormat,
    TimeFormatWithoutSeconds,
    MonthDayYearFormat,
    oneDayMilliseconds,
    oneHourInMilliseconds,
    validDaysCount,
    dailyViewLimitInWeeks,
    MonthsAndDaysUpperCase,
    GenderLabels,
    BonusTypeLabels,
    BonusAcceptanceTypeLabels,
    BonusResultTypeLabels,
    WidgetsCompStatus,
    CJAutoMapMode,
    CJModelRelationScopes,
    CJVisualizationDueDateTypeLabels,
    CJVisualizationUnlockConditionTypeLabels,
    CJArgumentValuesTypes,
    CustomerJourneyErrorCodes,
    CJReferenceInArgumentType,
    CJFormulaInArgumentType,
    CJStaticInArgumentType,
    DefaultDatesDict,
    MonthDayYearWithTimeFormat,
    DateToISOStringDateFormat,
    DateToISOStringDateFormatWithMonthName,
    DateToISOStringFormat,
    DateToISOStringFormatWithMilliseconds,
    FlowEditorElementsSetterActionTypes,
    CJUITypes,
    CJMenuItemDropDownTypes,
    excludeRightValueContentVisibilitiesOperators,
    CJDynamicDateInArgumentType,
    newVersionBuilderConfig,
    DayMonthFormat,
    timeSpanDefaultValue,
    DownloadPopoverViews,
    DownloadExtensions,
    partnerIdQueryNamespace,
    CustomersTypesLabels,
    DataTableColumnsCustomTypes,
    SortDirectionTypes,
    GoalDropdownOptions,
    Appearance,
    ViewMode,
    ActionsIcons,
    AuthIcons,
    CommonIcons,
    ModalsClassNames,
    ObjectTypesMap,
    ObjectTypeLabelMap,
    CampaignLogStatusLabels,
    ModalActionsConfig,
    CampaignStatusColors,
    ApprovalRequestStateLabels,
    ApprovalRequestStateOptions,
    ApprovalRequestStateColors,
    ApprovalRequestStateAlertTypes,
    ApprovalRequestStateMessages,
    ExportFileFormatLabels,
    CommunicationType,
    CommunicationStatus,
    CommunicationStatusLabelsMap,
    CommunicationPromotionType,
    CommunicationTypeLabels,
    CommunicationStatusLabels,
    CommunicationPromotionTypeLabels,
    RouteLeaveConfirmationType,
    EventNotifications,
    NotificationFields,
    PopUpUrlTypes,
    LogicTypesValues,
    FilterGroupLimit,
    CascadeRequestTypes,
    CascadeUITypes,
    EventLabels,
    EmailProviderTypeLabels,
    PromotionTypeLabels,
    NavigationActionTypes,
    ErrorMessageSymbolsCount,
    MaxTimeoutDelay,
    NotificationsDescriptionsLabels,
    DefaultLangId,
    defaultLogicFunction,
    clientIdTypeName,
    remainingTimeTypeName,
    DeliveryMethodTypes,
    PromotionTypesLabels,
    NotificationCampaignTypesLabels,
    BonusStateLabels,
    BonusTriggerType,
    TemplateInputTypes,
    DateToISOStringWithoutTimezoneFormat,
    ZERO_TIME_WITH_SECONDS,
    ZERO_TIME_WITHOUT_SECONDS,
    ReportPageType,
    ConfigurationEventStatus,
    ConfigurationStatusLabels,
    CommunicationAvailabilityScheduleTypeLabels,
    CommunicationAvailabilityScheduleTypes,
    MenuItemTypes,
    ExecutedReportParametersDataTypes,
    DisplayModeStyles,
    DeviceTabInfos,
    ModeTabInfos,
    DataTablePagingOptions,
    CampaignActivityDefaultPageSize,
    CJDependencyTypes,
    CompositeReportOperation,
    ClientsCountType,
    BooleanTypeLabels,
    WhatsAppTemplateStatusLabels,
    CustomerJourneyGroupBlockTypesValues,
    PartnerSettingsBaseKindTypes,
    PartnerSettingsValueTypes,
    PartnerStatusLabels,
    PartnerStatus,
    EmailProviderTypes,
    SortOrderIDOptions,
    DependencyObjectTypeLabels,
    GlobalEvents,
    AwardUsageTypeLabels,
    AwardUsageTypeLabelValueList,
    SubscriberSources,
    SEGMENT_FILTER_APPLICATION_COLUMN_ID,
    DisplayModeClassNames,
    DeviceTypeClassNames,
    ArchiveStates,
    ArchiveUnarchiveTypes,
    ClientColumnSets,
    MenuItemsTypes,
    MenuItemsLabels,
    RealtimePromotionTypes,
    RealtimePromotionTypeIcons,
    PromotionTypesDropdownData,
    CustomerJourneyInfoModalTitleLabels,
    SupportedComparisonFilterFieldsTypes,
    KPISources,
    ManageTagActionTypes,
    ManageTagActionList,
    ClientKPIColumnTypes,
    ApplicationColumnIds,
    TemplateEditorModes,
    TemplateEditorTypes,
    AvailabilityColors,
    PartnerKindsRadioGroupOptions,
    ColorVars,
    GACategoryEvents,
    DeniedTags,
    DynamicPopUpDeviceTypesLabels,
    ExpirationBonusModelTypes,
    ExpirationTypes,
    PushNotificationProviderKeys,
    AuthProvidersTypes,
    PromotionTypesIcons,
    BonusTriggerTypeEnum,
    HexOpacityCodes,
    DangerConfidencePercentage,
    BonusModelTypesConfigs,
    CampaignLogStatusIcons,
    BonusLabels,
    ExpirationLabels,
    BOOLEAN_DROPDOWN_OPTIONS_FOR_FILTER,
    BLOCKED_BY_SUBSCRIPTION_CLASSNAME,
    RecurrenceDataColumnsConfig,
    LOYALTY_POINT_CURRENCY,
    RADIO_SELECT_COLUMN_CONFIG,
    MULTI_SELECT_COLUMN_CONFIG,
};
